import React, { useState } from "react";
import { Card, Tabs, Typography, Slider, Space, Row, Col, Steps } from "antd";

const { Title, Paragraph } = Typography;

const VAEExplainer = () => {
  const [morphLevel, setMorphLevel] = useState(0);

  const renderIntro = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>What is a VAE?</span>}>
        <Paragraph style={{ fontSize: "16px" }}>
          A Variational Autoencoder (VAE) is like an imagination machine. It
          studies lots of pictures, learns their patterns. It understands what
          bananas, strawberries, and mangoes look like, and then can invent new
          ones that look real.
        </Paragraph>

        <Paragraph style={{ fontSize: "16px", marginBottom: 0 }}>
          It is a type of neural network that consists of two cooperating parts
          - Encoder and Decoder. Encoder takes the input and compresses it.
          Decoder then takes that compressed input and decompresses it back into
          the original input, trying to recreate it as closely as possible.
        </Paragraph>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Think of it like...</span>}
      >
        <div
          style={{
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #0071e3",
          }}
        >
          <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
            The Sketch Artist
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Imagine an artist who looks at a photo of a face, makes quick sketch notes:
            "oval face, blue eyes, brown hair, smiling". Later uses those notes
            to paint a portrait. The painting won't be pixel-perfect to the
            photo, but it captures the essential features.
          </Paragraph>
        </div>
      </Card>
    </Space>
  );

  const renderHistory = () => (
    <div className="fade-in">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card
          title={<span style={{ color: "#0071e3" }}>2013 - The Beginning</span>}
        >
          <Paragraph style={{ marginBottom: 0 }}>
            VAEs were introduced by scientists Kingma and Welling. The big idea
            was to let AI <strong>compress</strong> data into a smaller code and
            then <strong>decompress</strong> it back into something close to the
            original. This was one of the first ways AI could learn to generate
            new things rather than just memorize.
          </Paragraph>
        </Card>

        <Card
          title={
            <span style={{ color: "#0071e3" }}>2017–2020 - Phased Out</span>
          }
        >
          <Paragraph style={{ marginBottom: 0 }}>
            Around this time, <strong>GANs</strong> became popular because they
            could make sharper, more realistic images. Soon after,{" "}
            <strong>diffusion models</strong> (like Stable Diffusion and DALL·E)
            took over with even better quality and variety. VAEs were still
            useful for research, but they were no longer the main choice for
            consumer image generation.
          </Paragraph>
        </Card>

        <Card
          title={<span style={{ color: "#0071e3" }}>Today - Still Useful</span>}
        >
          <Paragraph style={{ marginBottom: 0 }}>
            VAEs are now mostly used in science, medicine, and music — places
            where having a <strong>structured compressed space</strong> is more
            important than having super sharp pictures.
          </Paragraph>
        </Card>
      </Space>
    </div>
  );

  const NetworkDiagram = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <div style={{ display: "flex", gap: "4px" }}>
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#595959",
          }}
        />
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#595959",
          }}
        />
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#595959",
          }}
        />
      </div>
    </div>
  );

  const renderProcessDisplay = () => {
    const stepsContent = [
      {
        title: "Step 1: Input",
        encoderContent: "🍎",
        decoderContent: "",
        description: "Red apple enters the Encoder for compression.",
      },
      {
        title: "Step 2: Compress",
        encoderContent: "🔒",
        decoderContent: "",
        description: "Encoder compresses the apple into a compact code.",
      },
      {
        title: "Step 3: Transfer",
        encoderContent: "",
        decoderContent: "🔒",
        description: "Compressed code is transferred to the Decoder.",
      },
      {
        title: "Step 4: Reconstruct",
        encoderContent: "",
        decoderContent: "🍎",
        description:
          "Decoder reconstructs the apple, but it may not be perfectly identical to the original due to information loss during compression.",
        isGrey: true,
      },
    ];

    const currentStep = stepsContent[morphLevel];

    return (
      <Card
        title={<span style={{ color: "#0071e3" }}>{currentStep.title}</span>}
        style={{ marginTop: "24px" }}
      >
        <Row gutter={[32, 32]} justify="center" align="middle">
          <Col xs={24} md={10}>
            <Card
              title={<span style={{ color: "#0071e3" }}>Encoder</span>}
              hoverable={false}
              style={{
                borderColor: currentStep.encoderContent ? "#0071e3" : "#d9d9d9",
                borderWidth: "2px",
              }}
            >
              <NetworkDiagram />
              <div
                style={{
                  fontSize: "48px",
                  textAlign: "center",
                  margin: "16px 0",
                  minHeight: "60px",
                }}
              >
                {currentStep.encoderContent}
              </div>
            </Card>
          </Col>
          <Col xs={24} md={10}>
            <Card
              title={<span style={{ color: "#0071e3" }}>Decoder</span>}
              hoverable={false}
              style={{
                borderColor: currentStep.decoderContent ? "#0071e3" : "#d9d9d9",
                borderWidth: "2px",
              }}
            >
              <NetworkDiagram />
              <div
                style={{
                  fontSize: "48px",
                  textAlign: "center",
                  margin: "16px 0",
                  minHeight: "60px",
                  filter: currentStep.isGrey ? "grayscale(100%)" : "none",
                }}
              >
                {currentStep.decoderContent}
              </div>
            </Card>
          </Col>
        </Row>
        <Paragraph
          style={{ marginTop: "24px", textAlign: "center", marginBottom: 0 }}
        >
          {currentStep.description}
        </Paragraph>
      </Card>
    );
  };

  const renderProcess = () => (
    <div className="fade-in">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Steps current={morphLevel} onChange={setMorphLevel}>
          <Steps.Step title="Input" description="Apple enters" />
          <Steps.Step title="Compress" description="Code generated" />
          <Steps.Step title="Transfer" description="To decoder" />
          <Steps.Step title="Reconstruct" description="Apple rebuilt" />
        </Steps>

        {renderProcessDisplay()}

        <Paragraph
          type="secondary"
          style={{ textAlign: "center", marginTop: "16px" }}
        >
          The visualizations show how VAEs compress and reconstruct data through
          neural networks.
        </Paragraph>
      </Space>
    </div>
  );

  const renderExamples = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>Morphing Faces</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          VAEs can smoothly change a face from young to old, or happy to
          serious.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Science & Music</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          VAEs help scientists invent new molecules and even create music that
          flows nicely.
        </Paragraph>
      </Card>
    </Space>
  );

  const tabItems = [
    {
      key: "intro",
      label: "What is a VAE?",
      children: renderIntro(),
    },
    {
      key: "history",
      label: "The Story",
      children: renderHistory(),
    },
    {
      key: "process",
      label: "Interactive Demo",
      children: renderProcess(),
    },
    {
      key: "examples",
      label: "Real Uses",
      children: renderExamples(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "3rem" }}
        className="fade-in-down"
      >
        <Title level={1}>Variational Autoencoders (VAEs)</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          AI that imagines new things
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="intro" items={tabItems} size="large" />
    </div>
  );
};

export default VAEExplainer;
